import type { AIAvatar, AIAvatarVoicePreset, AIVoice } from 'js/src/client';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import StoreORM from 'js/src/data/store-orm';
import { type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { createSelector } from 'reselect';

/**
 * Array.map will always return new reference causing unnecessary re-renders.
 * Use `createSelector` to automatically memoize the result of this selector.
 */
export const getAIVoices = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices,
    (aiVoices: StoreORMInterface<AIVoice, string>) => {
        const orm = new StoreORM<AIVoice, string>(aiVoices);
        return orm.map((voice) => voice.toJS());
    },
);

export function getAIVoiceIds(state: RootState): Immutable.List<string> {
    const orm = new StoreORM<AIVoice, string>(state.aiVoicesAndAvatars.aiVoices);
    return orm.getAllIds();
}

export function getAIVoiceById(state: RootState, id: string): ImmutableMap<AIVoice> | null {
    const orm = new StoreORM<AIVoice, string>(state.aiVoicesAndAvatars.aiVoices);
    const voice = orm.getByIdOrUndefined(id);
    return voice || null;
}

export function getAIAvatarVoicePresetsOrm(state: RootState) {
    return new StoreORM<AIAvatarVoicePreset>(state.aiVoicesAndAvatars.aiAvatarVoicePresets);
}

export function getAIAvatarVoicePresetIds(state: RootState): Immutable.List<number> {
    const orm = getAIAvatarVoicePresetsOrm(state);
    return orm.getAllIds();
}

export const getAIAvatarVoicePresetObjects = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiAvatarVoicePresets,
    (aiAvatarPreset: StoreORMInterface<AIAvatarVoicePreset>) => {
        const orm = new StoreORM<AIAvatarVoicePreset>(aiAvatarPreset);
        return orm.map((avatarPreset) => avatarPreset.toJS());
    },
);

export function getAIAvatarVoicePresetById(state: RootState, id: number) {
    const orm = getAIAvatarVoicePresetsOrm(state);
    if (!id || orm.getAllIds().size === 0) {
        return null;
    }
    return orm.getById(id);
}

export function getAIAvatarsOrm(state: RootState) {
    return new StoreORM<AIAvatar>(state.aiVoicesAndAvatars.aiAvatars);
}

export function getAIAvatarIds(state: RootState) {
    const orm = new StoreORM<AIAvatar>(state.aiVoicesAndAvatars.aiAvatars);
    return orm.getAllIds();
}

export function getAIAvatarById(state: RootState, id: number) {
    const orm = new StoreORM<AIAvatar>(state.aiVoicesAndAvatars.aiAvatars);
    return orm.getById(id);
}

export function getIsAIVoiceoverBatchComplete(state: RootState): boolean {
    return state.aiVoicesAndAvatars.aiVoiceoverBatchComplete;
}
